import {
  getGridStringOperators,
  GridColDef,
  GridFilterOperator,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import {
  getAllReports,
  getReportTypes,
  getAllResourceCalendars,
  UserKeys,
  getSharedDrivesByIdsQuery,
} from '../../../api/users-api';
import { ReportModel, ReportType, UserTier } from '../../../../types';
import React, { useMemo, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import ReportsGridToolbar from './reports-grid-toolbar';
import ReportActionsCell from './report-actions-cell';
import ReportsDeleteDialog from './reports-delete-dialog';
import ReportsPinMenu from './reports-pin-menu';
import * as analytics from '../../../shared/analytics/analytics.json';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { ModuleList } from '../../../shared/analytics/module-list';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import ReportsRunDialog from './reports-run-dialog';
import AvatarChip from '../../../shared/avatar-chip';
import { Messages } from '../../../utils/message-constants';
import { gPanelTiers } from '../../../shared/gPanel-tiers';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import { useUserSettings } from '../../../utils/use-user-settings';
import { useSearchParams } from 'react-router-dom';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import { useReportActions } from '../../../shared/hooks/use-report-actions';
import { useReportPinning } from '../../../shared/hooks/use-report-pinning';

import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Summarize from '@mui/icons-material/Summarize';

interface ReportsGridProps {
  toolsRoles: string[];
  changeLogRoles: string[];
  usersTier: UserTier;
}

type ReportGridRow =
  | (ReportModel & { isHeader?: false; headerTitle?: never })
  | (Omit<Partial<ReportModel>, 'id'> & {
      id: string;
      isHeader: true;
      headerTitle: string;
    });

export default function ReportsGrid({
  toolsRoles,
  changeLogRoles,
  usersTier,
}: ReportsGridProps) {
  const { createEvent } = useAnalytics();
  const [, setSearchParams] = useSearchParams();
  const { viewType } = useUserSettings();

  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const theme = useTheme();

  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'createdDate', sort: 'desc' },
  ]);

  const hasDomainPinPermission = useMemo(
    () => isAbleToSeeUIElement(toolsRoles, ToolsRoles.DomainPinReports.value),
    [toolsRoles]
  );

  const {
    pinMenuAnchorEl,
    reportForPinMenu,
    initialMenuPinnedState,
    personalPinnedReports,
    personalPinnedReportIds,
    isSavingDomainPin,
    isSavingPersonalPin,
    handlePinMenuClick,
    handlePinMenuClose,
    toggleDomainPin,
    togglePersonalPin,
  } = useReportPinning();

  const {
    data: reports,
    isLoading: reportsLoading,
    isFetching: reportsFetching,
  } = useQuery({
    queryKey: [UserKeys.Reports],
    queryFn: () => getAllReports(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('reports'),
    },
  });

  const { data: reportTypes, isLoading: reportTypesLoading } = useQuery({
    queryKey: [UserKeys.ReportTypes],
    queryFn: () => getReportTypes(),
  });

  const reportTypeToNiceNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    reportTypes?.forEach((rt) => {
      map[rt.name] = rt.niceName;
    });
    return map;
  }, [reportTypes]);

  const removedHiddenReports = useMemo(
    () => reportTypes?.filter((report) => report.category !== 'hidden') || [],
    [reportTypes]
  );

  const {
    handleStartReport,
    handleConfirmRunDialog,
    handleConfirmRun,
    isRunning,
    reportForRun,
    openRunConfirmDialog,
    closeRunConfirmDialog,
    changeLogMessage,
    setChangeLogMessage,
    openChangeLogDialog,
    closeChangeLogDialog,
    handleEditReport,
    openDeleteDialog,
    setOpenDeleteDialog,
  } = useReportActions({ toolsRoles, changeLogRoles, removedHiddenReports });

  const { data: resourceCalendars, isLoading: resourceCalendarsLoading } =
    useQuery({
      queryKey: [UserKeys.ResourceCalendars],
      queryFn: () => getAllResourceCalendars(),
      enabled: !!reports?.some(
        (report) => report.sourceType === 'Resource_Calendar'
      ),
    });

  const sharedDriveSources = useMemo(() => {
    const allSources =
      reports
        ?.filter((r) => r.sourceType === 'Shared_Drive')
        .flatMap((s) => s.sources || []) || [];

    return Array.from(new Set(allSources));
  }, [reports]);
  const { data: sharedDrives, isLoading: sharedDrivesLoading } = useQuery({
    ...getSharedDrivesByIdsQuery(sharedDriveSources),
    enabled: sharedDriveSources.length > 0,
  });

  const sharedDriveIdToNameMap = useMemo(() => {
    if (!sharedDrives) {
      return new Map<string, string>();
    }
    return new Map(sharedDrives.map((d) => [d.id, d.name]));
  }, [sharedDrives]);

  const calendarEmailToNameMap = useMemo(() => {
    if (!resourceCalendars) {
      return new Map<string, string>();
    }
    return new Map(
      resourceCalendars.map((c) => [c.resourceEmail, c.resourceName])
    );
  }, [resourceCalendars]);

  const customStringOperators: GridFilterOperator[] =
    getGridStringOperators().map((operator) => {
      if (operator.value === 'isEmpty') {
        return { ...operator, label: 'never ran' };
      }
      if (operator.value === 'isNotEmpty') {
        return { ...operator, label: 'completed run' };
      }
      return operator;
    });

  const getToolTipText = useCallback(
    (option: ReportType | undefined) => {
      if (option) {
        if (
          usersTier.rank <= gPanelTiers.starter.rank &&
          !option.includeInStarter
        ) {
          return Messages.Errors.Tier.Acting(gPanelTiers.standard.displayValue);
        } else if (
          !isAbleToSeeUIElement(toolsRoles, ToolsRoles.MaunalRunReport.value) &&
          !isAbleToSeeUIElement(toolsRoles, option.role)
        ) {
          const matchingRole = Object.keys(ToolsRoles).find(
            (key) => ToolsRoles[key].value === option.role
          );
          if (matchingRole) {
            return Messages.Errors.Permission.Acting(
              ToolsRoles[matchingRole].name
            );
          }
          return Messages.Errors.Permission.Acting(option.role);
        } else {
          return option.description;
        }
      } else {
        return '';
      }
    },
    [usersTier, toolsRoles]
  );

  const reportTypeTooltips = useMemo(() => {
    const map = new Map<string, string>();
    removedHiddenReports.forEach((reportType) => {
      map.set(reportType.name, getToolTipText(reportType));
    });
    return map;
  }, [removedHiddenReports, getToolTipText]);

  const personalPinnedReportIdsSet = useMemo(
    () => new Set(personalPinnedReportIds),
    [personalPinnedReportIds]
  );

  const toolsRoleMap = useMemo(() => {
    const map = new Map<string, (typeof ToolsRoles)[keyof typeof ToolsRoles]>();
    Object.values(ToolsRoles).forEach((role) => {
      map.set(role.value, role);
    });
    return map;
  }, []);

  const columns: GridColDef<ReportGridRow>[] = useMemo(
    () => [
      {
        field: 'createdDate',
        headerName: 'Created Date',
        width: 140,
        type: 'dateTime',
        valueGetter: (value) => {
          if (!value) return null;
          const date = new Date(value);
          return date;
        },
        valueFormatter: (params: Date) => {
          if (!params) return '';
          const formattedDate = dayjs(params).format('MMM D, YYYY, h:mm A');
          return formattedDate;
        },
        colSpan: (_, row) => {
          if (row.isHeader) return 999;
          return undefined;
        },
        renderCell: (params) => {
          if (params.row.isHeader) {
            return (
              <Typography
                variant="subtitle1"
                sx={{ m: 0, lineHeight: 'inherit' }}
              >
                {params.row.headerTitle}
              </Typography>
            );
          }
          return params.formattedValue;
        },
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        editable: false,
        sortable: true,
        filterable: true,
        flex: 2,
        renderCell: (params) => {
          if (params.row.isHeader) return null;
          return <AvatarChip email={params.value} type="User" />;
        },
      },
      {
        field: 'reportName',
        headerName: 'Report Name',
        flex: 2,
      },
      {
        field: 'reportType',
        headerName: 'Report Type',
        flex: 2,
        valueGetter: (value: string | null) => {
          if (!value) return null;
          return reportTypeToNiceNameMap[value] || value;
        },
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
      },
      {
        field: 'sources',
        headerName: 'Sources',
        flex: 3,
        display: 'flex',
        renderCell: (params) => {
          if (params.row.isHeader) return null;
          if (params.value) {
            return (
              <Stack flexDirection="row" gap={1} sx={{ overflowX: 'auto' }}>
                {params.value.map((target: string) => {
                  const sourceType = params.row.sourceType;
                  const type =
                    sourceType === 'User'
                      ? 'User'
                      : sourceType === 'Group'
                      ? 'Group'
                      : sourceType === 'gPanel_Role'
                      ? 'gPanel Role'
                      : sourceType === 'Resource_Calendar'
                      ? 'Resource Calendar'
                      : sourceType === 'Shared_Drive'
                      ? 'Shared Drive'
                      : 'Domain';

                  const label =
                    sourceType === 'Resource_Calendar'
                      ? calendarEmailToNameMap.get(target) || target
                      : sourceType === 'Shared_Drive'
                      ? sharedDriveIdToNameMap.get(target) || target
                      : target;
                  if (
                    (resourceCalendarsLoading &&
                      sourceType === 'Resource_Calendar') ||
                    (sharedDrivesLoading && sourceType === 'Shared_Drive')
                  ) {
                    return (
                      <Skeleton
                        key={target}
                        variant="rounded"
                        width={150}
                        height={20}
                      />
                    );
                  }

                  return <AvatarChip key={target} email={label} type={type} />;
                })}
              </Stack>
            );
          } else {
            return 'N/A';
          }
        },
      },
      {
        field: 'lastRunDate',
        headerName: 'Last Ran On',
        width: 140,
        type: 'dateTime',
        filterOperators: customStringOperators,
        valueGetter: (value, row) => {
          if (row?.isHeader) return null;
          if (!value) return null;
          const date = new Date(value as number);
          return date;
        },
        valueFormatter: (params: Date | null) => {
          if (!params || params === null) return 'Never Ran';
          const formattedDate = dayjs(params).format('MMM D, YYYY, h:mm A');
          return formattedDate;
        },
      },
      {
        field: 'scheduled',
        headerName: 'Is Scheduled',
        type: 'boolean',
        flex: 1,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 240,
        sortable: false,
        filterable: false,
        display: 'flex',
        renderCell: (params) => {
          if (params.row.isHeader || reportsLoading || reportsFetching)
            return null;

          const report = params.row;
          const reportType = removedHiddenReports.find(
            (type) => type.name === report.reportType
          );
          const tooltipText = reportTypeTooltips.get(report.reportType) || '';
          const canRunReport =
            !reportTypesLoading &&
            tooltipText === reportType?.description &&
            report.manualRun !== false;
          const cannotRunReason = canRunReport
            ? undefined
            : report.manualRun === false
            ? 'This report is scheduled'
            : tooltipText;

          const hasEditPermission = isAbleToSeeUIElement(
            toolsRoles,
            ToolsRoles.EditReport.value
          );
          const editTooltip = hasEditPermission
            ? 'Edit'
            : Messages.Errors.Permission.Acting(ToolsRoles.EditReport.name);

          const isDomainPinned = report.domainPinnedAt != null;
          const isPersonalPinned = personalPinnedReportIdsSet.has(report.id);
          const isPinned = isDomainPinned || isPersonalPinned;
          const isSavingPin =
            (isSavingDomainPin || isSavingPersonalPin) &&
            reportForPinMenu?.id === report.id;
          const rolePermission = reportType?.role
            ? toolsRoleMap.get(reportType.role)
            : undefined;

          return (
            <ReportActionsCell
              rolePermission={rolePermission}
              hasEditPermission={hasEditPermission}
              editTooltip={editTooltip}
              onEdit={() => handleEditReport(report.id)}
              reportStatus={report.status ?? 'IDLE'}
              canRunReport={canRunReport}
              cannotRunReason={cannotRunReason}
              onRun={() => handleStartReport(report)}
              isPinned={isPinned}
              isDomainPinned={isDomainPinned}
              isPersonalPinned={isPersonalPinned}
              isSavingPin={isSavingPin}
              onPin={(e) =>
                handlePinMenuClick(e as React.MouseEvent<HTMLElement>, report)
              }
            />
          );
        },
      },
    ],
    [
      customStringOperators,
      reportTypeToNiceNameMap,
      calendarEmailToNameMap,
      sharedDriveIdToNameMap,
      resourceCalendarsLoading,
      sharedDrivesLoading,
      reportsLoading,
      reportsFetching,
      removedHiddenReports,
      reportTypeTooltips,
      reportTypesLoading,
      toolsRoles,
      personalPinnedReportIdsSet,
      isSavingDomainPin,
      isSavingPersonalPin,
      reportForPinMenu?.id,
      toolsRoleMap,
      handleEditReport,
      handleStartReport,
      handlePinMenuClick,
    ]
  );

  const defaultGridState = {
    columns: {
      columnVisibilityModel: {
        description: false,
        scheduled: false,
      },
    },
  };

  const mergedReports = useMemo(() => {
    if (!reports) return [];

    const domainPinned = reports.filter((r) => r.domainPinnedAt != null);
    const otherReports = reports.filter((r) => r.domainPinnedAt == null);

    const sortFn = (a: ReportModel, b: ReportModel) => {
      if (sortModel.length === 0) return 0;

      const { field, sort } = sortModel[0];
      let v1 = a[field as keyof ReportModel];
      let v2 = b[field as keyof ReportModel];

      if (field === 'reportType') {
        v1 = reportTypeToNiceNameMap[String(v1)] || v1;
        v2 = reportTypeToNiceNameMap[String(v2)] || v2;
      }

      if (v1 == null && v2 != null) return sort === 'asc' ? -1 : 1;
      if (v1 != null && v2 == null) return sort === 'asc' ? 1 : -1;
      if (v1 === v2) return 0;

      if (typeof v1 === 'string' && typeof v2 === 'string') {
        return sort === 'asc' ? v1.localeCompare(v2) : v2.localeCompare(v1);
      }
      if (typeof v1 === 'number' && typeof v2 === 'number') {
        return v1 < v2 ? (sort === 'asc' ? -1 : 1) : sort === 'asc' ? 1 : -1;
      }
      if (typeof v1 === 'boolean' && typeof v2 === 'boolean') {
        return v1 === v2
          ? 0
          : v1
          ? sort === 'asc'
            ? -1
            : 1
          : sort === 'asc'
          ? 1
          : -1;
      }

      if (Array.isArray(v1) && Array.isArray(v2)) {
        const str1 = v1.join(', ');
        const str2 = v2.join(', ');
        return sort === 'asc'
          ? str1.localeCompare(str2)
          : str2.localeCompare(str1);
      }

      if (typeof v1 === 'object' && typeof v2 === 'object') {
        return 0;
      }

      return 0;
    };

    domainPinned.sort((a, b) => {
      const aPinnedAt = a.domainPinnedAt ?? undefined;
      const bPinnedAt = b.domainPinnedAt ?? undefined;

      if (aPinnedAt !== undefined && bPinnedAt !== undefined) {
        return bPinnedAt - aPinnedAt;
      }

      if (aPinnedAt !== undefined && bPinnedAt === undefined) return -1;

      if (aPinnedAt === undefined && bPinnedAt !== undefined) return 1;

      return sortFn(a, b);
    });

    const pinnedMap = new Map(
      personalPinnedReports.map((p) => [p.id, p.pinnedAt])
    );

    otherReports.sort((a, b) => {
      const aPinnedAt = pinnedMap.get(a.id);
      const bPinnedAt = pinnedMap.get(b.id);

      if (aPinnedAt !== undefined && bPinnedAt !== undefined) {
        return bPinnedAt - aPinnedAt;
      }

      if (aPinnedAt !== undefined && bPinnedAt === undefined) return -1;

      if (aPinnedAt === undefined && bPinnedAt !== undefined) return 1;

      return sortFn(a, b);
    });

    const finalRows: ReportGridRow[] = [];
    if (domainPinned.length > 0) {
      const headerDomainPinned: ReportGridRow = {
        id: 'header-domain-pinned',
        isHeader: true,
        headerTitle: 'Domain Pinned Reports',
      };
      finalRows.push(headerDomainPinned);
      finalRows.push(...domainPinned);
      const headerAllReports: ReportGridRow = {
        id: 'header-all-reports',
        isHeader: true,
        headerTitle: 'All Reports',
      };
      finalRows.push(headerAllReports);
    }

    finalRows.push(...otherReports);

    return finalRows;
  }, [reports, sortModel, personalPinnedReports, reportTypeToNiceNameMap]);

  const toolbarProps = useMemo(
    () => ({
      toolsRoles,
      setOpenDeleteDialog,
      selectedRows,
    }),
    [selectedRows, toolsRoles, setOpenDeleteDialog]
  );

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: useToolbar(ReportsGridToolbar, toolbarProps),
          ...createGridOverlays('Reports', <Summarize />),
        }}
        rows={mergedReports}
        columns={columns}
        loading={reportsLoading || reportsFetching}
        checkboxSelection
        disableRowSelectionOnClick
        defaultGridState={defaultGridState}
        gridKey={viewType.reportsGridStates?.activeGridState}
        onSortModelChange={(model) => setSortModel(model)}
        sortingMode="server"
        rowHeight={52}
        isRowSelectable={(params) => !params.row.isHeader}
        getRowClassName={(params) => {
          if (params.row.isHeader) return 'header-row';
          const report = params.row;
          if (report.domainPinnedAt != null) return 'domain-pinned-row';
          if (personalPinnedReportIdsSet.has(report.id))
            return 'personal-pinned-row';
          return '';
        }}
        sx={{
          // Header row styling - ensure left alignment
          '& .header-row': {
            pointerEvents: 'none',
          },
          '& .header-row .MuiDataGrid-cell': {
            borderBottom: 'none',
            display: 'flex',
            alignItems: 'center',
            fontWeight: 'normal',
            paddingLeft: '16px !important',
          },
          '& .header-row .MuiDataGrid-cellCheckbox': {
            display: 'none',
          },
          '&& .domain-pinned-row, && .domain-pinned-row .MuiDataGrid-cell': {
            backgroundColor: theme.palette.brandBlue.light,
          },
          '&& .personal-pinned-row, && .personal-pinned-row .MuiDataGrid-cell':
            {
              backgroundColor: theme.palette.primaryMint.light,
            },
          '&& .domain-pinned-row:hover, && .domain-pinned-row:hover .MuiDataGrid-cell, && .personal-pinned-row:hover, && .personal-pinned-row:hover .MuiDataGrid-cell':
            {
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
              backgroundImage:
                theme.palette.mode === 'dark'
                  ? 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))'
                  : 'linear-gradient(rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05))',
            },
        }}
        onRowClick={(row) => {
          if (row.row.isHeader) return;
          setSearchParams((prev) => {
            prev.set('selectedReport', row.row.id.toString());
            return prev;
          });
          createEvent(analytics.report_center.reports.viewed_report_details, {
            module: ModuleList.modules.reportCenter.name,
            section: ModuleList.modules.reportCenter.sections.reports,
          });
        }}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        gridStateKey="reportsGridStates"
      />

      {openRunConfirmDialog && reportForRun && reportTypes ? (
        <ReportsRunDialog
          open={openRunConfirmDialog}
          onClose={closeRunConfirmDialog}
          reportName={reportForRun.reportName}
          isRunning={isRunning}
          onConfirm={handleConfirmRunDialog}
        />
      ) : null}
      {openChangeLogDialog && reportForRun ? (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={closeChangeLogDialog}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={handleConfirmRun}
          mutateLoading={isRunning}
        />
      ) : null}
      {selectedRows.length > 0 && openDeleteDialog ? (
        <ReportsDeleteDialog
          open={openDeleteDialog}
          handleClose={() => {
            setOpenDeleteDialog(false);
          }}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      <ReportsPinMenu
        anchorEl={pinMenuAnchorEl}
        report={reportForPinMenu}
        initialPinnedState={initialMenuPinnedState}
        hasDomainPinPermission={hasDomainPinPermission}
        personalPinnedReportIds={personalPinnedReportIds}
        isSavingDomainPin={isSavingDomainPin}
        isSavingPersonalPin={isSavingPersonalPin}
        onClose={handlePinMenuClose}
        onToggleDomainPin={toggleDomainPin}
        onTogglePersonalPin={togglePersonalPin}
      />
    </>
  );
}
